import { useCallback, useRef, useState } from 'react';

import type { Blueprint } from '@rta/rta-blueprint-library';
import type { CaptureDesignParams, CaptureDesignResult, CapturedViews, UseHeadlessCaptureReturn } from './types';
import { captureDesignViews } from './capture-design-views';
import { HeadlessCaptureRenderer } from './headless-capture-renderer';
import { Kitchen3dController } from '../controller';

const sleep = (ms: number): Promise<void> => new Promise((resolve) => setTimeout(resolve, ms));

const EMPTY_VIEWS: CapturedViews = { front: '', side: '', back: '', overhead: '', perspective: '' };
const EMPTY_RESULT: CaptureDesignResult = { views: EMPTY_VIEWS, glb: null };

/**
 * Encapsulates the headless 3D capture pattern.
 *
 * Returns an async `captureDesign` function and a `CapturePortal` JSX element.
 * The portal renders nothing when idle and mounts a hidden `<Kitchen3d>` during capture.
 *
 * **Important -- Apple Silicon WebGL constraint:**
 * The hook does NOT manage `setContentPanelMode`. Callers must ensure no other
 * `<Kitchen3d>` canvas is mounted before calling `captureDesign`. On Apple Silicon,
 * two simultaneous WebGL contexts cause `GL_INVALID_VALUE: glGetProgramiv` errors.
 * Dispatch `setContentPanelMode(Designer)` and await a bounded teardown poll before the capture
 * loop if a `<Kitchen3d>` canvas might already be mounted.
 *
 * @param assetsBaseUrl - Optional base URL for 3D assets (e.g. `import.meta.env.VITE_ASSETS_BASE_URL`)
 */
export type UseHeadlessCaptureParams = {
	assetsBaseUrl?: string;
};

export const useHeadlessCapture = (params: UseHeadlessCaptureParams = {}): UseHeadlessCaptureReturn => {
	const { assetsBaseUrl } = params;

	const [activeBlueprint, setActiveBlueprint] = useState<Blueprint | null>(null);
	const [activeSkyKey, setActiveSkyKey] = useState<string | undefined>(undefined);
	const [activeFloorKey, setActiveFloorKey] = useState<string | undefined>(undefined);
	const resolveRef = useRef<((controller: Kitchen3dController) => void) | null>(null);
	const rejectRef = useRef<((error: Error) => void) | null>(null);

	const handleReady = useCallback((controller: Kitchen3dController): void => {
		resolveRef.current?.(controller);
		resolveRef.current = null;
		rejectRef.current = null;
	}, []);

	const handleError = useCallback((error: Error): void => {
		rejectRef.current?.(error);
		resolveRef.current = null;
		rejectRef.current = null;
	}, []);

	const captureDesign = useCallback(async (captureParams: CaptureDesignParams): Promise<CaptureDesignResult> => {
		const { blueprint, onViewCaptured, exportGlb, glbMaxTextureSize, glbOnly, views, initialSkyKey, initialFloorKey, designId, timeoutScale } = captureParams;

		let controller: Kitchen3dController;
		try {
			controller = await new Promise<Kitchen3dController>((resolve, reject) => {
				resolveRef.current = resolve;
				rejectRef.current = reject;
				setActiveSkyKey(initialSkyKey);
				setActiveFloorKey(initialFloorKey);
				setActiveBlueprint(blueprint);
			});
		} catch (err) {
			setActiveBlueprint(null);
			setActiveSkyKey(undefined);
			setActiveFloorKey(undefined);
			const message = err instanceof Error ? err.message : String(err);
			return { ...EMPTY_RESULT, error: message };
		}

		try {
			return await captureDesignViews({ tdController: controller, onViewCaptured, exportGlb, glbMaxTextureSize, glbOnly, views, designId, timeoutScale });
		} catch (err) {
			const message = err instanceof Error ? err.message : String(err);
			return { ...EMPTY_RESULT, error: message };
		} finally {
			setActiveBlueprint(null);
			setActiveSkyKey(undefined);
			setActiveFloorKey(undefined);
			await sleep(100); // allow WebGL context to tear down before next capture
		}
	}, []);

	const CapturePortal = activeBlueprint
		? <HeadlessCaptureRenderer blueprint={activeBlueprint} onReady={handleReady} onError={handleError} assetsBaseUrl={assetsBaseUrl} initialSkyKey={activeSkyKey} initialFloorKey={activeFloorKey} />
		: null;

	return { captureDesign, CapturePortal, isCapturing: activeBlueprint !== null };
};
